/*
 This file is part of GNU Taler
 (C) 2022-2024, 2026 Taler Systems S.A.

 GNU Taler is free software; you can redistribute it and/or modify it under the
 terms of the GNU General Public License as published by the Free Software
 Foundation; either version 3, or (at your option) any later version.

 GNU Taler is distributed in the hope that it will be useful, but WITHOUT ANY
 WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 A PARTICULAR PURPOSE.  See the GNU General Public License for more details.
*/

import {
  AbsoluteTime,
  Duration,
  HttpStatusCode,
  TalerErrorCode,
  TokenRequest,
  TranslatedString,
  assertUnreachable,
  createRFC8959AccessTokenEncoded,
} from "@gnu-taler/taler-util";
import {
  AsyncAction,
  RouteDefinition,
  useAsyncAction,
  useBankCoreApiContext,
  useTranslationContext,
} from "@gnu-taler/web-util/browser";
import { RefObject, VNode, h } from "preact";
import { useEffect, useRef, useState } from "preact/hooks";
import { useBankChallengeHandlerContext } from "../context/challenge.js";
import { useSessionState } from "../hooks/session.js";
import { AuthAlert, AuthCard, FieldError, PasswordField } from "./AuthForm.js";
import {
  PASSWORD_MAX_LENGTH,
  PasswordError,
  USERNAME_MAX_LENGTH,
  UsernameError,
  validatePassword,
  validateUsername,
} from "./auth-validation.js";

const TALER_SCREEN_ID = 104;

export const SESSION_DURATION = Duration.toTalerProtocolDuration(
  Duration.fromSpec({ minutes: 30 }),
);

interface LoginOperationOptions {
  onSuccess?(username: string): void;
  onFailure?(username: string): void;
}

export interface LoginOperation extends AsyncAction<
  [username: string, password: string, challengeIds?: string[]]
> {
  error?: TranslatedString;
  clearError(): void;
}

/** Create and persist a bank access token, including its MFA continuation. */
export function useLoginOperation(
  options: LoginOperationOptions = {},
): LoginOperation {
  const { i18n } = useTranslationContext();
  const session = useSessionState();
  const {
    lib: { bank: api },
  } = useBankCoreApiContext();
  const mfa = useBankChallengeHandlerContext();
  const [error, setError] = useState<TranslatedString>();

  const tokenRequest: TokenRequest = {
    scope: "readwrite",
    duration: SESSION_DURATION,
    refreshable: true,
  };

  const action = useAsyncAction<
    Awaited<ReturnType<typeof api.createAccessToken>>,
    [string, string, challengeIds?: string[]]
  >(
    (ct, username, password, challengeIds) =>
      api.createAccessToken(
        username,
        { type: "basic", username, password },
        tokenRequest,
        { challengeIds },
      ),
    {
      onResult: async (result, username, password) => {
        if (result.type === "ok") {
          mfa.cancel();
          session.logIn({
            username,
            token: createRFC8959AccessTokenEncoded(result.body.access_token),
            expiration: AbsoluteTime.fromProtocolTimestamp(
              result.body.expiration,
            ),
          });
          options.onSuccess?.(username);
          return;
        }

        let message: TranslatedString;
        switch (result.case) {
          case HttpStatusCode.Accepted:
            mfa.onNewChallenge(
              i18n.str`Identity verification.`,
              username,
              result.body,
              {
                running: action.running,
                cancel: action.cancel,
                run: (challengeIds) =>
                  action.run(username, password, challengeIds),
              },
            );
            return;
          case TalerErrorCode.GENERIC_FORBIDDEN:
            message = i18n.str`You do not have permission to access this account.`;
            break;
          case TalerErrorCode.BANK_ACCOUNT_LOCKED:
            message = i18n.str`This account is locked. Contact the bank administrator.`;
            break;
          case HttpStatusCode.Unauthorized:
            message = i18n.str`The password is incorrect.`;
            break;
          case HttpStatusCode.NotFound:
            message = i18n.str`Account not found.`;
            break;
          default:
            assertUnreachable(result);
        }
        setError(message);
        options.onFailure?.(username);
      },
      onError: (_cause, username) => {
        setError(
          i18n.str`Could not reach the bank. Check your connection and try again.`,
        );
        options.onFailure?.(username);
      },
    },
  );

  return {
    ...action,
    error,
    clearError: () => setError(undefined),
    run: async (username, password, challengeIds) => {
      setError(undefined);
      await action.run(username, password, challengeIds);
    },
  };
}

type LoginField = "username" | "password";

export interface LoginFormViewProps {
  bankName: string;
  initialUsername?: string;
  fixedUser?: boolean;
  reauthentication?: boolean;
  sessionExpired?: boolean;
  registrationNotice?: TranslatedString;
  operationError?: TranslatedString;
  running?: boolean;
  registrationsAllowed?: boolean;
  routeRegister?: RouteDefinition;
  onSubmit(username: string, password: string): void | Promise<void>;
  onSignOut?(): void | Promise<void>;
}

LoginFormView.SCREEN_ID = TALER_SCREEN_ID;
export function LoginFormView({
  bankName,
  initialUsername,
  fixedUser = false,
  reauthentication = false,
  sessionExpired = false,
  registrationNotice,
  operationError,
  running = false,
  registrationsAllowed = false,
  routeRegister,
  onSubmit,
  onSignOut,
}: LoginFormViewProps): VNode {
  const { i18n } = useTranslationContext();
  const [username, setUsername] = useState(initialUsername ?? "");
  const [password, setPassword] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [touched, setTouched] = useState<Partial<Record<LoginField, boolean>>>(
    {},
  );
  const usernameRef = useRef<HTMLInputElement | null>(null);
  const passwordRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    const initialField = fixedUser ? passwordRef.current : usernameRef.current;
    initialField?.focus({ preventScroll: true });
  }, [fixedUser]);

  const usernameError = translateUsernameError(
    validateUsername(username),
    i18n,
  );
  const passwordError = translatePasswordError(
    validatePassword(password, true),
    i18n,
  );

  const markTouched = (field: LoginField): void => {
    setTouched((current) => ({ ...current, [field]: true }));
  };

  const submit = async (event: Event): Promise<void> => {
    event.preventDefault();
    if (running) return;
    setSubmitted(true);
    if (usernameError || passwordError) {
      focusFirstInvalid(
        [usernameError, usernameRef],
        [passwordError, passwordRef],
      );
      return;
    }
    await onSubmit(username, password);
  };

  return (
    <AuthCard
      title={
        reauthentication
          ? i18n.str`Confirm your password`
          : i18n.str`Sign in to ${bankName}`
      }
      subtitle={
        reauthentication
          ? i18n.str`Enter the password for ${username} to continue.`
          : i18n.str`Use your bank account credentials to continue.`
      }
      footer={
        !reauthentication && registrationsAllowed && routeRegister ? (
          <span>
            <i18n.Translate>Need an account?</i18n.Translate>{" "}
            <a
              href={routeRegister.url({})}
              class="font-semibold text-primaryDark hover:text-onPrimaryContainer hover:underline"
            >
              <i18n.Translate>Create one</i18n.Translate>
            </a>
          </span>
        ) : undefined
      }
    >
      {sessionExpired ? (
        <AuthAlert type="warning">
          <i18n.Translate>
            Your session expired. Confirm your password to continue.
          </i18n.Translate>
        </AuthAlert>
      ) : undefined}
      {registrationNotice ? (
        <AuthAlert type="success">{registrationNotice}</AuthAlert>
      ) : undefined}
      {operationError ? (
        <AuthAlert type="error">{operationError}</AuthAlert>
      ) : undefined}

      <form
        class="space-y-5"
        noValidate
        onSubmit={(event) => void submit(event)}
        autoCapitalize="none"
        autoCorrect="off"
      >
        <div>
          <label
            htmlFor="username"
            class="block text-sm font-medium leading-6 text-onBackground"
          >
            <i18n.Translate>Username</i18n.Translate>
          </label>
          <div class="mt-2">
            <input
              ref={usernameRef}
              type="text"
              name="username"
              id="username"
              class="block w-full rounded-md border-0 py-1.5 text-onBackground shadow-sm ring-1 ring-inset ring-gray-300 read-only:bg-gray-100 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
              value={username}
              readOnly={fixedUser}
              enterkeyhint="next"
              autocomplete="username"
              maxLength={USERNAME_MAX_LENGTH}
              required
              aria-invalid={
                (submitted || !!touched.username) && !!usernameError
              }
              aria-describedby={
                (submitted || touched.username) && usernameError
                  ? "username-error"
                  : undefined
              }
              onInput={(event) => setUsername(event.currentTarget.value)}
              onBlur={() => markTouched("username")}
            />
            <FieldError
              id="username-error"
              message={usernameError}
              visible={submitted || !!touched.username}
            />
          </div>
        </div>

        <PasswordField
          id="password"
          name="password"
          label={i18n.str`Password`}
          value={password}
          error={passwordError}
          showError={submitted || !!touched.password}
          autoComplete="current-password"
          inputRef={passwordRef}
          enterKeyHint="go"
          onInput={setPassword}
          onBlur={() => markTouched("password")}
        />

        <div class={reauthentication ? "flex justify-between gap-3" : ""}>
          {reauthentication && onSignOut ? (
            <button
              type="button"
              class="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-semibold text-onBackground shadow-sm hover:bg-gray-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-600"
              disabled={running}
              onClick={() => void onSignOut()}
            >
              <i18n.Translate>Sign out</i18n.Translate>
            </button>
          ) : undefined}
          <button
            type="submit"
            class="flex min-w-28 flex-1 justify-center rounded-md bg-primary px-3 py-2 text-sm font-semibold text-onPrimary shadow-sm hover:bg-primaryHover hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary disabled:cursor-wait disabled:bg-gray-300"
            disabled={running}
            aria-busy={running}
          >
            {running ? (
              <i18n.Translate>Signing in…</i18n.Translate>
            ) : reauthentication ? (
              <i18n.Translate>Continue</i18n.Translate>
            ) : (
              <i18n.Translate>Sign in</i18n.Translate>
            )}
          </button>
        </div>
      </form>
    </AuthCard>
  );
}

/** Collect and submit login data. */
LoginForm.SCREEN_ID = TALER_SCREEN_ID;
export function LoginForm({
  currentUser,
  fixedUser,
  routeRegister,
  registrationNotice,
  onSuccess,
}: {
  fixedUser?: boolean;
  currentUser?: string;
  routeRegister?: RouteDefinition;
  registrationNotice?: TranslatedString;
  onSuccess?(username: string): void;
}): VNode {
  const session = useSessionState();
  const { config } = useBankCoreApiContext();
  const sessionUser =
    session.state.status !== "loggedOut" ? session.state.username : undefined;
  const onlyThisUser = fixedUser || session.state.status !== "loggedOut";
  const login = useLoginOperation({ onSuccess });

  return (
    <LoginFormView
      bankName={config.bank_name ?? "Bank"}
      initialUsername={currentUser ?? sessionUser}
      fixedUser={onlyThisUser}
      reauthentication={session.state.status !== "loggedOut"}
      sessionExpired={session.state.status === "expired"}
      registrationNotice={registrationNotice}
      operationError={login.error}
      running={login.running}
      registrationsAllowed={config.allow_registrations ?? false}
      routeRegister={routeRegister}
      onSubmit={(username, password) => login.run(username, password, [])}
      onSignOut={session.logOut}
    />
  );
}

export function translateUsernameError(
  error: UsernameError | undefined,
  i18n: ReturnType<typeof useTranslationContext>["i18n"],
): TranslatedString | undefined {
  switch (error) {
    case undefined:
      return undefined;
    case "missing":
      return i18n.str`Enter your username.`;
    case "invalid":
      return i18n.str`Use letters, numbers, or any of these characters: - . _ ~`;
    case "too-long":
      return i18n.str`The username must not exceed 126 characters.`;
    default:
      assertUnreachable(error);
  }
}

export function translatePasswordError(
  error: PasswordError | undefined,
  i18n: ReturnType<typeof useTranslationContext>["i18n"],
): TranslatedString | undefined {
  switch (error) {
    case undefined:
      return undefined;
    case "missing":
      return i18n.str`Enter your password.`;
    case "too-short":
      return i18n.str`The password must contain at least 8 characters.`;
    case "too-long":
      return i18n.str`The password must not exceed ${PASSWORD_MAX_LENGTH} characters.`;
    default:
      assertUnreachable(error);
  }
}

function focusFirstInvalid(
  ...fields: Array<[TranslatedString | undefined, RefObject<HTMLInputElement>]>
): void {
  fields.find(([error]) => !!error)?.[1].current?.focus();
}
